import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import CasinoLogo from '@/assets/icons/casinoLogo';
import { NAV_BAR_HEIGHT } from '@/components/AppNavBar';
import { LineSeparator } from '@/components/LineSeparator';
import { SocialCurrencyToggle } from '@/components/SocialCurrencyToggle';
import { SocialWalletBalanceButton } from '@/components/SocialWalletBalanceButton';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useFreeSpinGames } from '@/feature/lobby-casino/hooks/use-free-spin-games';
import { StickyCellRendererComponent } from '@/feature/lobby/components/sticky-item/StickyCellRendererComponent';
import { StickyWrapper, useStickyScrollHandler } from '@/feature/lobby/hooks/use-sticky-translation';
import { LobbyProductSwitcherChipsHandle } from '@/feature/product-transition/components/LobbyProductSwitcherChipsHandle';
import { LobbyWrapper, productSwitcherStyles } from '@/feature/product-transition/components/LobbyWrapper';
import { PRODUCT_CHIPS_HEIGHT } from '@/feature/product-transition/components/ProductSwitcherChips';
import { LobbyCarousel } from '@/feature/promohub/components/LobbyCarousel';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useIsDesktopWeb } from '@/feature/responsive-design/hooks/use-is-desktop-web';
import { WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT, WhatsNewLobbyCard } from '@/feature/whats-new/components/WhatsNewLobbyCard';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { useWhatsNewVisibility } from '@/feature/whats-new/hooks/use-whats-new-visibility';
import { usePrevious } from '@/hooks/use-previous';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { useCasinoCategoryGames } from '../hooks/use-casino-category-games';
import { useFilteredLobbyGames } from '../hooks/use-casino-games';
import { useCasinoJackpotPoolPolling } from '../hooks/use-casino-jackpot-pool';
import { useCasinoLobbyPagination } from '../hooks/use-casino-lobby-pagination';
import { useRecentlyPlayedGames } from '../hooks/use-recently-played-games';
import { getLayoutComponent } from '../utils/utils';
import { EmptyGamesMessage } from './EmptyGamesMessage';
import { FavoritesEmptyState } from './FavoritesEmptyState';
import { GridListToggleLayout } from './Layout/GridListToggleLayout';
import { CasinoLayoutSkeleton } from './Layout/LayoutSkeleton';
import { TabBar } from './TabBar';

const TAB_PADDING = 12;
const POPULAR_GAMES_CATEGORY_NAME = 'featured games';

type CasinoCategoryListProps = {
    listRef: React.RefObject<Animated.ScrollView | null>;
    isLobby: boolean;
    category?: string;
    selectedCategoryId: string | null;
    setSelectedCategoryId: (id: string | null) => void;
};

export const CasinoCategoryList = ({
    listRef,
    isLobby,
    category,
    selectedCategoryId,
    setSelectedCategoryId,
}: CasinoCategoryListProps) => {
    const insets = useSafeAreaInsets();
    const { height } = useWindowDimensions();
    const { t } = useTranslation('casino');
    const { onScroll, scrollDirection, scrollOffset, scrollVelocity } = useStickyScrollHandler();
    const { hasUnseen } = useWhatsNewVisibility();
    const setLobbyReady = useWhatsNewStore(state => state.actions.setLobbyReady);

    const isDesktopWeb = useIsDesktopWeb();

    // Fetch all data here
    const { data: lobbyData, isLoading: isTabBarLoading } = useFilteredLobbyGames('Lobby');
    const popularGamesLayout = lobbyData.find(layout => layout.name.toLowerCase() === POPULAR_GAMES_CATEGORY_NAME);
    const { data: freeSpinGamesData, isLoading: isFreeSpinGamesLoading } = useFreeSpinGames();

    const hasFreeSpinsCategory = (freeSpinGamesData?.items.length ?? 0) >= 1;
    const {
        data: categoryData,
        isLoading,
        isFetchingNextPage,
        favoriteGamesData,
        isFavoriteGamesPending,
        hasFavoritesCategory,
        isFavoritesCategory,
        isFreeSpinsCategory,
    } = useCasinoCategoryGames({ isLobby, category });
    const { data: recentlyPlayedData, isPending: isRecentlyPlayedPending } = useRecentlyPlayedGames();
    const handleScrollPagination = useCasinoLobbyPagination({ isLobby, category });
    useCasinoJackpotPoolPolling();

    const whatsNewCardHeight = hasUnseen ? WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT : 0;
    const lobbyHeaderHeight = isDesktopWeb ? 0 : PRODUCT_CHIPS_HEIGHT + NAV_BAR_HEIGHT;
    const stickyTabSectionOffset = insets.top + lobbyHeaderHeight - TAB_PADDING + whatsNewCardHeight;

    useEffect(() => {
        if (!isTabBarLoading) {
            setLobbyReady(true);
        }
    }, [isTabBarLoading, setLobbyReady]);

    // Animate to top when category changes
    const prevCategory = usePrevious(category);
    useEffect(() => {
        if (scrollOffset.value > stickyTabSectionOffset && category !== prevCategory) {
            listRef.current?.scrollTo({ y: stickyTabSectionOffset, animated: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stickyTabSectionOffset, category, prevCategory]);

    const appNavbarStickPosition = useSharedValue(0);
    const handleStickPosition = useSharedValue(0);

    const onNavbarLayout = useCallback(
        (event: any) => {
            appNavbarStickPosition.value = event.nativeEvent.layout.y - insets.top;
        },
        [appNavbarStickPosition, insets.top]
    );

    const onHandleChipsLayout = useCallback(
        (event: any) => {
            handleStickPosition.value = event.nativeEvent.layout.y + PRODUCT_CHIPS_HEIGHT;
        },
        [handleStickPosition]
    );

    const renderCategoryLayout = useCallback(
        (layout: (typeof categoryData)[0], index: number, totalLayouts: number) => {
            const LayoutComponent = getLayoutComponent(layout.flags[0]);
            const isLastItem = index === totalLayouts - 1;

            const baseProps = {
                data: layout.items,
                layoutHeaderProps: {
                    title: layout.name,
                    categoryId: layout.id,
                    onSelectId: setSelectedCategoryId,
                    isLobby,
                },
            };

            return (
                <Box key={`${layout.id}-${index}`}>
                    <LayoutComponent {...baseProps} isFetchingNextPage={isFetchingNextPage} />
                    <Box pt="s24" pb={isLastItem ? 's16' : undefined}>
                        {!isLastItem ? <LineSeparator /> : null}
                    </Box>
                </Box>
            );
        },
        [isLobby, isFetchingNextPage, setSelectedCategoryId]
    );

    const renderedLayouts = categoryData ? [...categoryData] : [];

    if (isLobby && recentlyPlayedData) {
        renderedLayouts.splice(1, 0, recentlyPlayedData);
    }

    if (isLobby && hasFavoritesCategory && favoriteGamesData) {
        const favoriteInsertPosition = recentlyPlayedData ? 4 : 3;
        const limitedFavoriteGamesData = {
            ...favoriteGamesData,
            items: favoriteGamesData.items.slice(0, 10),
        };
        renderedLayouts.splice(Math.min(favoriteInsertPosition, renderedLayouts.length), 0, limitedFavoriteGamesData);
    }

    let favoritesFallbackLayout = recentlyPlayedData?.items.length ? recentlyPlayedData : undefined;
    if (!favoritesFallbackLayout && !isRecentlyPlayedPending && popularGamesLayout) {
        favoritesFallbackLayout = { ...popularGamesLayout, name: t('popular_games'), flags: ['featured-row'] };
    }

    const listContent = (
        <>
            {!isDesktopWeb ? (
                <>
                    <Box zIndex={101} onLayout={onHandleChipsLayout}>
                        <LobbyProductSwitcherChipsHandle
                            handleStickPosition={handleStickPosition}
                            scrollDirection={scrollDirection}
                            scrollOffset={scrollOffset}
                            scrollVelocity={scrollVelocity}
                        />
                    </Box>
                    <Box>
                        <Row
                            justifyContent="space-between"
                            px="s16"
                            alignItems="center"
                            height={48}
                            backgroundColor={ds.colorToken.background.neutral.base}
                        >
                            <CasinoLogo />
                            <Row gap="s8">
                                <SocialWalletBalanceButton />
                                <SocialCurrencyToggle />
                            </Row>
                        </Row>
                    </Box>
                </>
            ) : null}
            <WhatsNewLobbyCard />
            {!isDesktopWeb ? (
                <StickyCellRendererComponent shouldBeSticky={true} style={styles.appNavBar} onLayout={onNavbarLayout}>
                    <StickyWrapper
                        scrollOffset={scrollOffset}
                        scrollVelocity={scrollVelocity}
                        initialPosition={appNavbarStickPosition}
                        scrollDirection={scrollDirection}
                    >
                        <MaxWidthWrapper>
                            <Box pt="s14" backgroundColor={ds.colorToken.background.neutral.base}>
                                <TabBar
                                    data={lobbyData || []}
                                    showFavoritesTab={hasFavoritesCategory}
                                    showFreeSpinsTab={hasFreeSpinsCategory}
                                    selectedId={selectedCategoryId}
                                    onSelectId={setSelectedCategoryId}
                                    isLoading={isTabBarLoading}
                                />
                            </Box>
                        </MaxWidthWrapper>
                    </StickyWrapper>
                </StickyCellRendererComponent>
            ) : null}
            {isLobby ? (
                <>
                    <Box paddingVertical={'s12'}>
                        <LobbyCarousel style={styles.lobbyCarousel} />
                    </Box>
                    <LineSeparator />
                </>
            ) : null}
            {isFreeSpinsCategory ? (
                isFreeSpinGamesLoading ? (
                    <CasinoLayoutSkeleton />
                ) : freeSpinGamesData?.items.length ? (
                    <GridListToggleLayout
                        data={freeSpinGamesData.items}
                        layoutHeaderProps={{
                            title: t('all_free_spins'),
                            categoryId: freeSpinGamesData.id,
                            onSelectId: setSelectedCategoryId,
                            isLobby: false,
                        }}
                    />
                ) : (
                    <EmptyGamesMessage
                        title={t('no_free_spins_available')}
                        description={t('no_free_spins_available_description')}
                    />
                )
            ) : isFavoritesCategory ? (
                isFavoriteGamesPending ? (
                    <CasinoLayoutSkeleton />
                ) : favoriteGamesData?.items.length ? (
                    <GridListToggleLayout
                        data={favoriteGamesData.items}
                        layoutHeaderProps={{
                            title: t('all_favorites'),
                            categoryId: favoriteGamesData.id,
                            onSelectId: setSelectedCategoryId,
                            isLobby: false,
                        }}
                    />
                ) : (
                    <FavoritesEmptyState fallbackLayout={favoritesFallbackLayout} />
                )
            ) : isLoading ? (
                <CasinoLayoutSkeleton />
            ) : !isLoading && categoryData && categoryData.length > 0 ? (
                <Box minHeight={isDesktopWeb ? undefined : height - stickyTabSectionOffset}>
                    {renderedLayouts.map((layout, index) =>
                        renderCategoryLayout(layout, index, renderedLayouts.length)
                    )}
                </Box>
            ) : !isLoading ? (
                <EmptyGamesMessage />
            ) : null}
        </>
    );

    if (isDesktopWeb) {
        return listContent;
    }

    return (
        <LobbyWrapper>
            <Animated.ScrollView
                ref={listRef}
                onScroll={onScroll}
                scrollEventThrottle={16}
                onMomentumScrollEnd={handleScrollPagination}
                showsVerticalScrollIndicator={showScrollIndicator}
                contentContainerStyle={[
                    productSwitcherStyles.lobbyListContentContainerStyle,
                    isWeb ? { minHeight: height + PRODUCT_CHIPS_HEIGHT } : styles.contentContainer,
                ]}
            >
                {listContent}
            </Animated.ScrollView>
        </LobbyWrapper>
    );
};

const styles = StyleSheet.create({
    appNavBar: {
        zIndex: 110,
        top: 0,
    },
    lobbyCarousel: {
        paddingBottom: 12,
    },
    contentContainer: {
        minHeight: '100%',
    },
});
